/**
 * SubmitOutcomePanel — renders the result of POST /v1/drafts/:id/submit.
 *
 * Three states:
 *   - Success                    → mini-receipt + Collect Payment section.
 *                                  Print Receipt only enables once a CASH /
 *                                  CARD / TRANSFER payment has been authorised
 *                                  AND captured against the mail item.
 *   - PROHIBITED_ITEMS_BLOCKED   → red panel; keywords listed; "Edit draft" CTA.
 *   - SOFT_WARNINGS_NEED_ACK     → yellow panel + acknowledgement checkbox + "Submit anyway".
 *
 * Payment flow:
 *   1. Operator picks a payment method (Cash / Card / Transfer).
 *   2. We POST /v1/payments to authorise, then POST /v1/payments/:id/capture
 *      in the same handler — counter payments settle immediately, there is no
 *      separate hold-then-capture step.
 *   3. On success the section flips to "Paid — receipt #..." and unlocks the
 *      Print Receipt + New Registration buttons.
 *   4. On failure the operator can retry with the same or a different method.
 */
import { type JSX, useState } from 'react';
import { Printer, FilePlus2, Loader2, Banknote, CreditCard, ArrowLeftRight, CheckCircle2, AlertCircle } from 'lucide-react';

import { capturePayment, createPayment, type PaymentMethod, type PaymentSummary } from '../../lib/api/payments';
import { ApiError } from '../../lib/api/client';
import { formatMoney } from './formatMoney';

import type {
  DraftPayload,
  ProhibitedItemHit,
  SubmitDraftResult,
} from '../../lib/api/types';

interface SubmitOutcomePanelProps {
  result: SubmitDraftResult;
  /**
   * Latest draft payload — used to render the mini-receipt summary on the
   * success state. Not required for the prohibited / soft-warning paths
   * (they render their own copy regardless of payload).
   */
  payload?: DraftPayload;
  onRegisterAnother: () => void;
  onEditDraft: () => void;
  onAcknowledgeAndResubmit: () => void;
}

export function SubmitOutcomePanel({
  result,
  payload,
  onRegisterAnother,
  onEditDraft,
  onAcknowledgeAndResubmit,
}: SubmitOutcomePanelProps): JSX.Element {
  if ('trackingNumber' in result) {
    return (
      <SuccessPanel
        result={result}
        payload={payload}
        onRegisterAnother={onRegisterAnother}
      />
    );
  }

  if (result.reason === 'PROHIBITED_ITEMS_BLOCKED') {
    return (
      <div
        data-testid="submit-outcome-hard"
        className="rounded border border-red-300 bg-red-50 p-6"
      >
        <h3 className="text-lg font-semibold text-red-900">Submission blocked</h3>
        <p className="mt-1 text-sm text-red-800">
          The package contains prohibited items. Resolve the highlighted items
          below before re-submitting.
        </p>
        <HitList hits={result.hits} severityClass="text-red-900" />
        <button
          type="button"
          onClick={onEditDraft}
          data-testid="submit-outcome-edit"
          className="mt-4 rounded bg-red-700 px-4 py-2 text-sm font-semibold text-white"
        >
          Edit draft
        </button>
      </div>
    );
  }

  return <SoftPanel hits={result.hits} onAcknowledge={onAcknowledgeAndResubmit} />;
}

interface SuccessPanelProps {
  result: { id: string; trackingNumber: string };
  payload?: DraftPayload;
  onRegisterAnother: () => void;
}

function SuccessPanel({ result, payload, onRegisterAnother }: SuccessPanelProps): JSX.Element {
  const senderName = payload?.sender?.name ?? '';
  const receiverName = payload?.receiver?.name ?? '';
  const senderCity = payload?.sender?.city ?? '';
  const receiverCity = payload?.receiver?.city ?? '';
  const amountNgn = payload?.pricing?.amountNgn ?? '';

  const receiptHref = `/mail/receipt/${encodeURIComponent(result.trackingNumber)}`;
  const hasAmount = amountNgn !== '' && Number(amountNgn) > 0;

  const [paymentStatus, setPaymentStatus] = useState<
    'PENDING' | 'IN_PROGRESS' | 'PAID' | 'FAILED'
  >(hasAmount ? 'PENDING' : 'PAID');
  const [payment, setPayment] = useState<PaymentSummary | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  async function collect(method: PaymentMethod): Promise<void> {
    setPaymentStatus('IN_PROGRESS');
    setPaymentError(null);
    try {
      const authorised = await createPayment({
        reference: { kind: 'MAIL_ITEM', id: result.id },
        amount: amountNgn,
        currency: 'NGN',
        methodKind: method,
        idempotencyKey: `mail-${result.id}-${method}`,
      });
      const captured = await capturePayment(authorised.id);
      setPayment(captured);
      setPaymentStatus('PAID');
    } catch (err) {
      const detail =
        err instanceof ApiError
          ? err.status === 403
            ? 'You do not have permission to take payments at this counter.'
            : err.message
          : err instanceof Error
            ? err.message
            : 'Payment failed. Please try again.';
      setPaymentError(detail);
      setPaymentStatus('FAILED');
    }
  }

  const paid = paymentStatus === 'PAID';

  return (
    <div
      data-testid="submit-outcome-success"
      className="rounded border border-emerald-300 bg-emerald-50 p-6"
    >
      <h3 className="text-lg font-semibold text-emerald-900">Mail registered ✓</h3>
      <p className="mt-1 text-sm text-emerald-800">Tracking number</p>
      <p
        data-testid="submit-outcome-tracking"
        className="font-mono text-2xl font-bold text-emerald-900"
      >
        {result.trackingNumber}
      </p>

      <dl
        data-testid="submit-outcome-summary"
        className="mt-4 grid grid-cols-1 gap-2 rounded border border-emerald-200 bg-white/70 p-4 text-sm text-emerald-900 sm:grid-cols-2"
      >
        {senderName ? (
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-emerald-700">From</dt>
            <dd className="font-medium">
              {senderName}
              {senderCity ? <span className="text-emerald-700"> · {senderCity}</span> : null}
            </dd>
          </div>
        ) : null}
        {receiverName ? (
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-emerald-700">To</dt>
            <dd className="font-medium">
              {receiverName}
              {receiverCity ? <span className="text-emerald-700"> · {receiverCity}</span> : null}
            </dd>
          </div>
        ) : null}
        {amountNgn ? (
          <div className="sm:col-span-2">
            <dt className="text-[10px] uppercase tracking-wide text-emerald-700">
              {paid ? 'Total paid' : 'Amount due'}
            </dt>
            <dd
              data-testid="submit-outcome-amount"
              className="text-base font-bold font-mono"
            >
              {formatMoney(amountNgn)}
            </dd>
          </div>
        ) : null}
      </dl>

      {hasAmount ? (
        <div className="mt-4 rounded border border-emerald-200 bg-white p-4">
          {paid ? (
            <div
              data-testid="submit-outcome-paid"
              className="flex items-start gap-2 text-sm text-emerald-800"
            >
              <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="font-semibold text-emerald-900">Payment received</p>
                {payment ? (
                  <p className="text-xs text-emerald-700">
                    Receipt #{payment.paymentNumber} · {payment.methodKind} ·{' '}
                    {formatMoney(payment.amountCaptured ?? payment.amountAuthorized)}
                  </p>
                ) : (
                  <p className="text-xs text-emerald-700">No amount due.</p>
                )}
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                Collect payment
              </p>
              <p className="mt-0.5 text-xs text-emerald-700">
                Confirm the customer's method to settle this registration.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <PayButton
                  method="CASH"
                  label="Cash"
                  Icon={Banknote}
                  busy={paymentStatus === 'IN_PROGRESS'}
                  onClick={() => collect('CASH')}
                />
                <PayButton
                  method="CARD"
                  label="Card / POS"
                  Icon={CreditCard}
                  busy={paymentStatus === 'IN_PROGRESS'}
                  onClick={() => collect('CARD')}
                />
                <PayButton
                  method="TRANSFER"
                  label="Bank transfer"
                  Icon={ArrowLeftRight}
                  busy={paymentStatus === 'IN_PROGRESS'}
                  onClick={() => collect('TRANSFER')}
                />
              </div>
              {paymentError ? (
                <div
                  data-testid="submit-outcome-payment-error"
                  className="mt-3 flex items-start gap-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-800"
                >
                  <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                  <span>{paymentError}</span>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        <a
          data-testid="submit-outcome-print"
          href={paid ? receiptHref : undefined}
          target="_blank"
          rel="noreferrer noopener"
          aria-disabled={!paid}
          onClick={(e) => {
            if (!paid) e.preventDefault();
          }}
          className={`inline-flex items-center gap-2 rounded px-4 py-2 text-sm font-semibold text-white ${
            paid
              ? 'bg-emerald-700 hover:bg-emerald-800'
              : 'bg-emerald-300 cursor-not-allowed'
          }`}
        >
          <Printer className="h-4 w-4" /> Print receipt
        </a>
        <button
          type="button"
          onClick={onRegisterAnother}
          disabled={!paid}
          data-testid="submit-outcome-register-another"
          className={`inline-flex items-center gap-2 rounded border px-4 py-2 text-sm font-semibold ${
            paid
              ? 'border-emerald-700 text-emerald-800 hover:bg-emerald-100'
              : 'border-emerald-300 text-emerald-400 cursor-not-allowed'
          }`}
        >
          <FilePlus2 className="h-4 w-4" /> New registration
        </button>
      </div>
    </div>
  );
}

interface PayButtonProps {
  method: PaymentMethod;
  label: string;
  Icon: typeof Banknote;
  busy: boolean;
  onClick: () => void;
}

function PayButton({ label, Icon, busy, onClick }: PayButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-2 rounded border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
      {label}
    </button>
  );
}

interface HitListProps {
  hits: ProhibitedItemHit[];
  severityClass: string;
}

function HitList({ hits, severityClass }: HitListProps): JSX.Element {
  return (
    <ul className={`mt-3 list-disc space-y-1 pl-6 text-sm ${severityClass}`}>
      {hits.map((hit, idx) => (
        <li key={`${hit.field}-${hit.keyword}-${idx}`}>
          <span className="font-mono">{hit.keyword}</span>
          <span className="ml-2 text-xs uppercase tracking-wide">
            ({hit.field} · {hit.severity})
          </span>
        </li>
      ))}
    </ul>
  );
}

interface SoftPanelProps {
  hits: ProhibitedItemHit[];
  onAcknowledge: () => void;
}

function SoftPanel({ hits, onAcknowledge }: SoftPanelProps): JSX.Element {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <div
      data-testid="submit-outcome-soft"
      className="rounded border border-amber-300 bg-amber-50 p-6"
    >
      <h3 className="text-lg font-semibold text-amber-900">Soft warning</h3>
      <p className="mt-1 text-sm text-amber-800">
        The package contains items that need operator acknowledgement.
      </p>
      <HitList hits={hits} severityClass="text-amber-900" />

      <label className="mt-4 flex items-start gap-2 text-sm text-amber-900">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          data-testid="submit-outcome-soft-ack"
          className="mt-0.5"
        />
        <span>I acknowledge the warnings and confirm the contents are accurate.</span>
      </label>

      <button
        type="button"
        onClick={onAcknowledge}
        disabled={!acknowledged}
        data-testid="submit-outcome-soft-resubmit"
        className="mt-4 rounded bg-amber-700 px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-300"
      >
        Submit anyway
      </button>
    </div>
  );
}
